import React from 'react'

import PendingActionsIcon from '@mui/icons-material/PendingActions'
import { Alert, AppBar, alpha, Button, Stack, TextField, Toolbar, Typography } from '@mui/material'
import { getFeatureLabel } from 'features/customers/utils/features'

import { NOTE_MAX } from '@albacross/types'

type Props = {
  count: number
  invalid: string[]
  saving: boolean
  note: string
  onNoteChange: (note: string) => void
  onSave: () => void
  onDiscard: () => void
}

const names = (keys: string[]) => keys.map(key => getFeatureLabel(key) || key).join(', ')

const CustomerFeaturesSaveBar = ({ count, invalid, saving, note, onNoteChange, onSave, onDiscard }: Props) => (
  <AppBar
    color="default"
    elevation={8}
    position="fixed"
    sx={{
      top: 'auto',
      bottom: 0,
      // Tint over an opaque ground, not a translucent colour, or the table scrolls visibly under it.
      borderTop: 3,
      borderColor: 'warning.main',
      backgroundColor: 'background.paper',
      backgroundImage: theme => {
        const tint = alpha(theme.palette.warning.main, 0.12)

        return `linear-gradient(${tint}, ${tint})`
      },
    }}>
    <Toolbar sx={{ justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap', py: 1 }}>
      {!!invalid.length && (
        <Alert severity="error" sx={{ py: 0, maxWidth: 640 }}>
          {names(invalid)} needs to be a whole number of zero or more, or empty to fall back to the plan.
        </Alert>
      )}

      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', color: 'warning.dark' }}>
        <PendingActionsIcon fontSize="small" />
        <Typography sx={{ fontWeight: 600 }} variant="body2">
          {count} unsaved {count === 1 ? 'change' : 'changes'}
        </Typography>
      </Stack>

      <TextField
        disabled={saving}
        onChange={event => onNoteChange(event.target.value)}
        placeholder="Why? e.g. cut to 10 at their request"
        size="small"
        slotProps={{ htmlInput: { maxLength: NOTE_MAX } }}
        sx={{ minWidth: 320, backgroundColor: 'background.paper' }}
        value={note}
      />

      <Stack direction="row" spacing={1}>
        <Button disabled={saving} onClick={onDiscard}>
          Discard
        </Button>
        <Button disabled={saving || !!invalid.length} onClick={onSave} variant="contained">
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </Stack>
    </Toolbar>
  </AppBar>
)

export default CustomerFeaturesSaveBar
